<template>
  <!-- A gridded card carries the same automation badges a compact tile does
       (owner, 24.10.2026: *"we need device automation widget indicators on
       device cards"*), so the card faces are not the one surface that hides
       what drives the device. The wrapper is the corner those badges anchor
       to; the card itself fills it. -->
  <div v-if="effectiveCardSize" class="device-widget-card">
    <!-- Every type without a bespoke face gets the shared card: its picture (or
         glyph), its name and its first reading. The device's own sheet keeps the
         controls, so tapping a card opens it. -->
    <GenericDeviceCard
      v-if="genericCard"
      :device="device"
      :size="effectiveCardSize"
      @open="$emit('open-control-modal', device)" />

    <component
      v-else
      :is="widgetComponent"
      v-bind="{ ...widgetProps, ...cardAwareProps }"
      @toggle="$emit('toggle', device)"
      @open-control-modal="$emit('open-control-modal', device)"
      @view-stream="$emit('view-stream')"
      @view-details="$emit('view-details')"
      @value-update="handleValueUpdate" />

    <DeviceAutomationOverlay
      :device-id="device.id"
      :device-values="device.deviceValues"
      :kind="automationOverlayKind" />
  </div>

  <!-- The non-card modes (a page's icon pin, the control sheet, a touch grid)
       draw the widget's own markup. -->
  <component
    v-else
    :is="widgetComponent"
    v-bind="{ ...widgetProps, ...cardAwareProps }"
    @toggle="$emit('toggle', device)"
    @open-control-modal="$emit('open-control-modal', device)"
    @view-stream="$emit('view-stream')"
    @view-details="$emit('view-details')"
    @value-update="handleValueUpdate" />
</template>

<script setup lang="ts">
import { computed } from 'vue';

// Unified per-type widgets
import LightWidget from './devices/LightWidget.vue';
import GenericDeviceCard from './devices/GenericDeviceCard.vue';
import DeviceAutomationOverlay, { type DeviceAutomationKind } from './devices/DeviceAutomationOverlay.vue';
import SwitchWidget from './devices/SwitchWidget.vue';
import CoverWidget from './devices/CoverWidget.vue';
import ThermostatWidget from './devices/ThermostatWidget.vue';
import SensorWidget from './devices/SensorWidget.vue';
import WaterTankWidget from './devices/WaterTankWidget.vue';
import LockWidget from './devices/LockWidget.vue';
import CameraDeviceWidget from './devices/CameraDeviceWidget.vue';
import GenericWidget from './devices/GenericWidget.vue';
import FanWidget from './devices/FanWidget.vue';
import VentilationWidget from './devices/VentilationWidget.vue';
import AlarmWidget from './devices/AlarmWidget.vue';
import BinarySensorWidget from './devices/BinarySensorWidget.vue';
import StrikeLockWidget from './devices/StrikeLockWidget.vue';
import ClimateWidget from './devices/ClimateWidget.vue';
import SpaWaterWidget from './devices/SpaWaterWidget.vue';
import SaunaWidget from './devices/SaunaWidget.vue';
import ComputerWidget from './devices/ComputerWidget.vue';
import NetworkDeviceWidget from './devices/NetworkDeviceWidget.vue';
import ClientPresenceWidget from './devices/ClientPresenceWidget.vue';
import PresenceSensorWidget from './devices/PresenceSensorWidget.vue';
import SynologyWidget from './devices/SynologyWidget.vue';
import GarageDoorWidget from './devices/GarageDoorWidget.vue';
import VacuumWidget from './devices/VacuumWidget.vue';
import MowerWidget from './devices/MowerWidget.vue';
import HeatDemandWidget from './devices/HeatDemandWidget.vue';
import HeatPumpWidget from './devices/HeatPumpWidget.vue';

import { robotKindOf } from '../utils/robotState';
import { isHeatPumpDevice, isTankDevice } from './devices/deviceWidgetTypes';

// Media player: thin wrapper around MediaPlayerControl
import MediaPlayerWidget from './devices/MediaPlayerWidget.vue';

import type { Device, DeviceValueWithReferences } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  deviceValues?: DeviceValueWithReferences[];
  colSpan?: number;
  rowSpan?: number;
  /** The card size the page's grid publishes — handed to the widgets that draw
   *  a card face (light, cover), so the same cell follows the size toggle. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'card',
  deviceValues: () => [],
  cardSize: undefined,
});

const emit = defineEmits<{
  toggle: [device: Device];
  'open-control-modal': [device: Device];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
  delete: [];
}>();

const DEVICE_TYPE_ALIASES: Record<string, string> = {
  socket: 'switch',
  media_player: 'media',
  touch_panel: 'media',
  motorized_lock: 'lock',
  strike_lock: 'strike_lock',
  alarm_zone: 'alarm',
  alarm_output: 'alarm',
  meter: 'sensor',
  power_meter: 'sensor',
  power_meter_3phase: 'sensor',
  water_meter: 'sensor',
  weather_station: 'sensor',
  computer_desktop: 'computer',
  // The heat demand controller's own class — its hub control type is `switch`,
  // and the category is only what the card face and the automation overlay read
  // it through. The widget itself is routed before this fold (see below), so the
  // alias never hands it the generic switch card; it only keeps a row that
  // carries the class id in `deviceControl` on the switch's card-face rules.
  heat_demand_controller: 'switch',
};

const LAYOUT_AWARE_DEVICE_TYPES = new Set(['light', 'cover', 'thermostat', 'climate', 'sauna', 'garage_door']);

function normalizeDeviceTypeCategory(type: string | null | undefined): string {
  const normalized = String(type ?? '').toLowerCase();
  if (!normalized) {
    return 'generic';
  }

  if (normalized.startsWith('light_')) return 'light';
  if (normalized.startsWith('shade_')) return 'cover';

  return DEVICE_TYPE_ALIASES[normalized] ?? normalized;
}

// ── Device type resolution ────────────────────────────────────────────────
/**
 * The robot family comes FIRST (card t_390bc438): both of its classes carry
 * `controlType: "switch"`, so the control fold below would land a robot vacuum
 * on the generic switch card — the device's own type is the only field that
 * names the machine (`robotKindOf` reads it, and the class icon as the second
 * net). Everything else resolves exactly as it always did.
 */
const deviceTypeCategory = computed(() => {
  const robot = robotKindOf(props.device);
  if (robot === 'vacuum') return 'vacuum';
  if (robot === 'mower') return 'lawn_mower';

  if (props.device.deviceControl) {
    return normalizeDeviceTypeCategory(props.device.deviceControl);
  }
  return normalizeDeviceTypeCategory(props.device.controlType || props.device.deviceType);
});

// ── Widget routing ────────────────────────────────────────────────────────
/**
 * The tank classes are the one family whose own card face is not in
 * `CARD_FACE_DEVICE_TYPES` by category — the hub reports them as `sensor`, and
 * a row created before the handler was recorded carries no control at all.
 * Resolved once, so routing and the card-face props can never disagree.
 */
const isTank = computed(() => isTankDevice(props.device));

/** The heat pump reports as `climate` and would otherwise get the thermostat dial. */
const isHeatPump = computed(() => isHeatPumpDevice(props.device));

const widgetComponent = computed(() => {
  // The heat demand controller's own sheet (owner, 2026-10-22: *"we need a
  // custom widget which would display zone demands, with real zone names"*).
  // Its class presents as `controlType: "switch"`, so the fold below would hand
  // it the generic switch card — which can show the contact but knows nothing
  // of the zones behind it. The control spelling is checked too: a device
  // created before the class existed carries the handler type there.
  const heatDemandClass = String(props.device.deviceType ?? '').toLowerCase();
  const heatDemandControl = String(props.device.deviceControl ?? '').toLowerCase();
  if (heatDemandClass === 'heat_demand_controller' || heatDemandControl === 'heat_demand_controller') {
    return HeatDemandWidget;
  }

  // A tank is a device in its own right (owner, 26.10.2026: *"we need typed
  // watertanks here… new widget?"*). The hub files it under the sensor control,
  // so the switch below would hand it the generic reading card — which has no
  // picture of a tank, no fill driven by the level, and no place for a refill
  // request or a plant's blower. Its own class ids, and nothing else, route here.
  if (isTank.value) {
    return WaterTankWidget;
  }

  // A heat pump is a plant, not a dial (owner, 26.10.2026: *"heat_pump, let's do
  // heat pumps now"*). Its class presents as `climate`, so the switch below would
  // hand it the room thermostat — which has no picture of a machine, no
  // compressor, and no flow and return.
  if (isHeatPump.value) {
    return HeatPumpWidget;
  }

  switch (deviceTypeCategory.value) {
    case 'light':
      return LightWidget;
    case 'cover':
      return CoverWidget;
    // The garage door's own surface (owner, 2026-10-16: "we should make a new
    // widget for garage doors"). It used to alias onto the cover widget, which
    // drew a shade's drag-to-set percentage for a door the hub drives with a
    // `command` (its `position` is `settable: false`).
    case 'garage_door':
      return GarageDoorWidget;
    // The two robots (card t_390bc438): the demo's Robot Vacuum and Robot
    // Mower, modelled on Matter's Robotic Vacuum Cleaner shape — a state, a
    // battery, a mode, a job's progress and the RVC verbs, which the generic
    // switch card had no room for.
    case 'vacuum':
      return VacuumWidget;
    case 'lawn_mower':
      return MowerWidget;
    case 'switch':
      return SwitchWidget;
    case 'thermostat':
      return ThermostatWidget;
    case 'sensor':
      return SensorWidget;
    case 'binary_sensor':
      return BinarySensorWidget;
    case 'lock':
      return LockWidget;
    case 'camera':
      return CameraDeviceWidget;
    case 'media':
      return MediaPlayerWidget;
    case 'fan':
      return FanWidget;
    case 'ventilation':
      return VentilationWidget;
    case 'alarm':
      return AlarmWidget;
    case 'strike_lock':
      return StrikeLockWidget;
    case 'climate':
      return ClimateWidget;
    case 'sauna':
      return SaunaWidget;
    case 'pool':
      return SpaWaterWidget;
    case 'hot_tub':
      return SpaWaterWidget;
    case 'computer':
      return ComputerWidget;
    case 'network':
      return NetworkDeviceWidget;
    case 'presence':
      return ClientPresenceWidget;
    case 'presence_sensor':
      return PresenceSensorWidget;
    case 'synology':
      return SynologyWidget;
    default:
      return GenericWidget;
  }
});

const widgetProps = computed(() => {
  const baseProps = {
    device: props.device,
    mode: props.mode,
    fluid: props.fluid,
    displayMode: props.displayMode,
  };

  if (!LAYOUT_AWARE_DEVICE_TYPES.has(deviceTypeCategory.value)) {
    return baseProps;
  }

  return {
    ...baseProps,
    colSpan: props.colSpan,
    rowSpan: props.rowSpan,
  };
});

/** Only the widgets with a card face read the page's size — the rest keep their
 *  own markup, so an unknown prop never lands on their root element. */
const CARD_FACE_DEVICE_TYPES = new Set([
  'light',
  'cover',
  'binary_sensor',
  'lock',
  'strike_lock',
  'switch',
  'sensor',
  'generic',
  'camera',
  'thermostat',
  'climate',
  'fan',
]);

/** Card mode always draws a card: a page that publishes no size gets the bare
 *  min face, so no surface falls back to the retired button tile. */
const effectiveCardSize = computed(() => props.cardSize ?? (props.mode === 'card' ? 'min' : undefined));

const cardAwareProps = computed(() =>
  effectiveCardSize.value && (CARD_FACE_DEVICE_TYPES.has(deviceTypeCategory.value) || isTank.value || isHeatPump.value)
    ? { cardSize: effectiveCardSize.value }
    : {},
);

// ── The shared card every other type falls back to ────────────────────────
// The panel-style widgets (sauna, spa, computer, network, synology, robots,
// media, garage door, alarm, presence, ventilation) keep their own drawing
// inside the control sheet; on a page that grids cards they are drawn as the
// same card as everything else, and a tap opens the sheet.
const genericCard = computed(
  () =>
    Boolean(effectiveCardSize.value) &&
    !(CARD_FACE_DEVICE_TYPES.has(deviceTypeCategory.value) || isTank.value || isHeatPump.value),
);

/* The card's automation overlay leads with the indicator its compact tile
   leads with — a switch/light its timer or motion mark, a cover its shade
   protections, a binary sensor its alarm zone; every other type shows the
   badge stack alone. */
const automationOverlayKind = computed<DeviceAutomationKind>(() => {
  const category = deviceTypeCategory.value;
  if (category === 'light') return 'light';
  if (category === 'switch') return 'switch';
  if (category === 'cover') return 'cover';
  if (category === 'binary_sensor') return 'binary-sensor';
  return 'other';
});

function handleValueUpdate(...args: unknown[]) {
  emit('value-update', args[0] as string, args[1]);
}
</script>

<style scoped>
/* ── The card's own wrapper ─────────────────────────────────────────────────── */
/* Page grids size the wrapper (a flex cell stretches it, a CSS grid row does
   the same); the card face inside fills it exactly as it did when it was the
   cell's direct child. `relative` is what the automation badges hang off. */
.device-widget-card {
  position: relative;
  display: flex;
  min-width: 0;
  height: 100%;
}

.device-widget-card > :first-child {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
